import { createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit'
import { initialContactState } from 'store/contact/initial.ts'
import {
  getContactByEmail,
  getEmailByContactId,
  getMyContact,
  updateAccount,
  updateBillingContract,
  updateContact,
  updateInstalment
} from 'store/contact/thunks'
import { normalizeContactData } from 'store/contact/thunks/utils.ts'
import { GetContactErrorPayload } from 'store/contact/thunks/types.ts'
import { AccountRecordTypeIds } from 'types/contacts.ts'
import {
  BillingContractWithServiceContracts as BillingContract,
  getProductFromProductCode,
  PLENTY_EL_PRODUCT_CODES
} from '@boltenergy-be/shared'
import posthog from 'posthog-js'
import { contractSubscriptionDateOlderThanOneDay } from 'utils/contracts.ts'

export const contactSlice = createSlice({
  name: 'contact',
  initialState: initialContactState,
  reducers: {
    setSelectedContract: (state, { payload }: PayloadAction<BillingContract['contractNumber']>) => {
      const billingContract = state.billingContracts[payload]

      // The contract number can be stale (contract removed, account restructured) or absent from a partially loaded store.
      // Keeping the current selection is preferable to crashing the reducer and taking down the whole app.
      if (!billingContract) return

      state.selected.billingContract = billingContract.contractNumber
      state.selected.account = billingContract.customerNumber
      state.selected.opportunity = billingContract.opportunitySfId

      posthog.setPersonProperties({
        lastViewedContract: billingContract.contractNumber
      })
    },

    updateSelectedServiceContracts: (state, { payload }: PayloadAction<BillingContract['serviceContracts']>) => {
      state.billingContracts[state.selected.billingContract].serviceContracts.electricity = payload.electricity
      if (state.billingContracts[state.selected.billingContract].serviceContracts.gas) {
        state.billingContracts[state.selected.billingContract].serviceContracts.gas = payload.gas
      }
    },

    clearUser: () => {
      return initialContactState
    }
  },
  selectors: {
    selectIsProducerAccount: (state) => {
      const { selected, accounts } = state

      if (!selected.account || Object.keys(accounts).length === 0 || !(selected.account in accounts)) {
        return false
      }

      return accounts?.[selected.account]?.recordType === AccountRecordTypeIds.PRODUCER
    },
    selectContactByEmail: (state) => state.contactByEmail,
    selectContactEmails: (state) => state?.contactEmails,
    selectContactEmail: (state, contactId: string) => state?.contactEmails?.[contactId],
    selectHasPlentyProduct: (state) =>
      state.billingContracts &&
      Object.values(state.billingContracts).some((contract) =>
        PLENTY_EL_PRODUCT_CODES.includes(contract.serviceContracts.electricity.detail.productCode)
      ),
    selectCurrentBillingContract: ({ billingContracts, selected }) => billingContracts[selected.billingContract],
    selectCurrentServiceContracts: ({ billingContracts, selected }) => billingContracts[selected.billingContract].serviceContracts,
    selectCurrentAccount: ({ accounts, selected }) => accounts[selected.account],
    selectCurrentProduct: ({ billingContracts, selected }) => {
      const currentElectricityContract = billingContracts[selected.billingContract].serviceContracts.electricity
      const currentProduct = currentElectricityContract.detail.productCode
      return getProductFromProductCode(currentProduct)
    },
    selectCurrentOpportunity: (contactStore) => {
      const opportunityId = contactStore?.selected?.opportunity ?? selectCurrentBillingContract({ contact: contactStore })?.opportunitySfId
      if (!opportunityId) return undefined

      const opportunities = contactStore.opportunities
      return opportunities && opportunityId && opportunityId in opportunities ? opportunities[opportunityId] : undefined
    },
    selectCurrentAccountHasContractWithSubscriptionDateOlderThanOneDay: ({ accounts, billingContracts, selected }) => {
      const currentAccount = accounts[selected.account]
      if (!currentAccount || !billingContracts || (billingContracts && !Object.keys(billingContracts)?.length)) return false

      return currentAccount.billingContracts?.some(
        (contractNumber) =>
          contractNumber in billingContracts &&
          contractSubscriptionDateOlderThanOneDay(billingContracts[contractNumber].serviceContracts.electricity)
      )
    }
  },
  extraReducers: (builder) => {
    builder.addCase(updateContact.fulfilled, (state, { payload }) => {
      const normalizedData = normalizeContactData(payload.contact)

      state.success = true
      state.loading = false
      state.contact = normalizedData.contact
    })

    builder.addCase(updateAccount.fulfilled, (state, { payload }) => {
      const updatedAccount = payload.account

      state.success = true
      state.loading = false
      state.accounts[updatedAccount.customerNumber] = {
        ...state.accounts[updatedAccount.customerNumber],
        ...updatedAccount
      }
    })

    builder.addCase(updateBillingContract.fulfilled, (state, { payload }) => {
      const updatedPartialBillingContract = payload.billingContract

      return {
        ...state,
        success: true,
        loading: false,
        billingContracts: {
          ...state.billingContracts,
          [updatedPartialBillingContract.contractNumber]: {
            ...state.billingContracts[updatedPartialBillingContract.contractNumber],
            ...updatedPartialBillingContract
          }
        }
      }
    })

    builder.addCase(updateInstalment.fulfilled, (state, { payload }) => {
      const { billingContractId, electricity, gas } = payload

      state.success = true
      state.loading = false
      state.billingContracts[billingContractId].serviceContracts = {
        electricity,
        gas
      }
    })

    builder.addCase(getEmailByContactId.pending, (state, { meta }) => {
      state.contactEmails[meta.arg.contactId] = {
        data: null,
        error: null,
        loading: true,
        success: false
      }
    })

    builder.addCase(getEmailByContactId.rejected, (state, { payload, meta }) => {
      state.contactEmails[meta.arg.contactId] = {
        data: null,
        error: payload || 'Unkown error',
        loading: false,
        success: false
      }
    })

    builder.addCase(getEmailByContactId.fulfilled, (state, { payload, meta }) => {
      state.contactEmails[meta.arg.contactId] = {
        data: payload.email,
        error: null,
        loading: false,
        success: true
      }
    })

    builder.addCase(getMyContact.pending, (state) => {
      state.myContact = {
        error: null,
        loading: true,
        success: false
      }
    })

    builder.addCase(getMyContact.rejected, (state, { payload }) => {
      state.myContact.loading = false
      state.myContact.success = false

      if (typeof payload === 'string') {
        state.myContact.error = payload
      }
    })

    builder.addCase(getMyContact.fulfilled, (state) => {
      state.myContact = {
        error: null,
        loading: false,
        success: true
      }
    })

    builder.addCase(getContactByEmail.pending, (state) => {
      state.contactByEmail = {
        error: null,
        errorData: [],
        loading: true,
        success: false
      }
    })

    builder.addCase(getContactByEmail.rejected, (state, { payload }) => {
      state.contactByEmail = {
        error: payload?.error || null,
        errorData: payload?.errorData || [],
        loading: false,
        success: false
      }
    })

    builder.addCase(getContactByEmail.fulfilled, (state) => {
      state.contactByEmail = {
        error: null,
        errorData: [],
        loading: false,
        success: true
      }
    })

    // IS PENDING
    builder.addMatcher(
      isPending(getMyContact, getContactByEmail, updateContact, updateAccount, updateBillingContract, updateInstalment),
      (state) => {
        state.loading = true
        state.error = null
        state.success = false
      }
    )

    // IS FULFILLED
    builder.addMatcher(isFulfilled(getMyContact, getContactByEmail), (state, { payload }) => {
      state.initialLoaded = true
      state.loading = false
      state.error = null
      state.success = true

      state.ignoredBillingContracts = payload.ignoredBillingContracts
      state.accounts = payload.normalizedStoreData.accounts
      state.billingContracts = payload.normalizedStoreData.billingContracts
      state.contact = payload.normalizedStoreData.contact
      state.opportunities = payload.normalizedStoreData.opportunities

      if (payload.selected) {
        state.selected = payload.selected
      }
    })

    // IS REJECTED
    builder.addMatcher(
      isRejected(getMyContact, getContactByEmail, updateContact, updateAccount, updateBillingContract, updateInstalment),
      (state, { payload }) => {
        const { error } = payload as GetContactErrorPayload

        state.error = error
        state.loading = false
        state.success = false
      }
    )
  }
})

export const { setSelectedContract, clearUser, updateSelectedServiceContracts } = contactSlice.actions

export const {
  selectContactByEmail,
  selectContactEmail,
  selectContactEmails,
  selectCurrentAccount,
  selectCurrentAccountHasContractWithSubscriptionDateOlderThanOneDay,
  selectCurrentBillingContract,
  selectCurrentServiceContracts,
  selectHasPlentyProduct,
  selectIsProducerAccount,
  selectCurrentOpportunity,
  selectCurrentProduct
} = contactSlice.selectors

export default contactSlice.reducer
